/**
 * Live CSK witness inspector — surfaces the runtime artifacts that prove a
 * Neutralization Theorem is wired into a demo, not merely mentioned.
 *
 * Most CSK demos historically imported only the `*_META` constant for chrome.
 * This module exposes a `GuaranteeWitnessPanel` that takes a live
 * `Guarantee<Structure, Violation>` and (optionally) a `StackingCertificate`
 * and renders both with the same visual treatment used elsewhere in the
 * shell — so the viewer sees the actual SSM type, mode, tier, and SDP
 * stacking witness alongside the demo it governs.
 *
 * Composition: builds on `WitnessCard` from `@csk/demo-ui` so the surface
 * inherits the shared card / status-badge / motion treatment.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React, { useMemo } from "react";
import type { Guarantee, GuaranteeMeta } from "@csk/core";
import type { StackingCertificate } from "@csk/core";
import { ShieldCheck, Layers, Sigma } from "lucide-react";
import { safeStringify } from "../utils/safeStringify.js";
import {
  GRAY,
  SAFE,
  PRIMARY,
  WARN,
  THEOREM_COLOR,
  THEOREM_BG,
  type CanonicalTheoremName,
} from "../design-system/tokens.js";
import { type WitnessCardField, WitnessCard } from "./WitnessCard.js";
import { WitnessEnter } from "./animations/WitnessEnter.js";

// ── Mode tone mapping ──────────────────────────────────────────

// Keys are the string values stored on `GuaranteeMeta.mode` — the
// brand at the type level erases to a plain string at runtime.
const MODE_LABEL: Readonly<Record<string, { tone: string; bg: string; copy: string }>> = {
  immune: {
    tone: SAFE[700],
    bg: SAFE[100],
    copy: "IMMUNE — violations have no type representation",
  },
  inert: {
    tone: PRIMARY[700],
    bg: PRIMARY[100],
    copy: "INERT — violations exist but cannot compose into exploits",
  },
  contained: {
    tone: WARN[700],
    bg: WARN[100],
    copy: "CONTAINED — violations bounded by resource budget",
  },
};

// ── Public props ───────────────────────────────────────────────

export interface GuaranteeWitnessPanelProps<S, V> {
  /** Live runtime guarantee instance — its `meta` and `structure` are surfaced. */
  readonly guarantee: Guarantee<S, V>;
  /** Optional SDP stacking certificate for the guarantee's compositionality lane. */
  readonly stacking?: StackingCertificate;
  /** Optional structured payload captured at the seam (the most recent witness value). */
  readonly capturedWitness?: {
    readonly label: string;
    readonly typeNotation: string;
    readonly value: string;
  };
  /** Whether the seam is currently active — controls the entry animation key. */
  readonly active?: boolean;
}

// ── Component ──────────────────────────────────────────────────

/**
 * Render the live state of a CSK guarantee: its `GuaranteeMeta`, an
 * optional captured witness from the most recent seam interaction, and
 * (when supplied) the SDP `StackingCertificate` that licenses safe
 * composition with the rest of the deployed kernel.
 */
export function GuaranteeWitnessPanel<S, V>({
  guarantee,
  stacking,
  capturedWitness,
  active = false,
}: GuaranteeWitnessPanelProps<S, V>): React.JSX.Element {
  const { meta } = guarantee;
  const modeLabel = MODE_LABEL[meta.mode] ?? {
    tone: GRAY[700],
    bg: GRAY[100],
    copy: meta.mode,
  };
  // meta.name is a plain string at the type level — guard at runtime so
  // an off-registry theorem still renders against a sensible fallback.
  const isCanonical = (n: string): n is CanonicalTheoremName =>
    Object.prototype.hasOwnProperty.call(THEOREM_COLOR, n);
  const theoremColor = isCanonical(meta.name) ? THEOREM_COLOR[meta.name] : PRIMARY[500];
  const theoremBg = isCanonical(meta.name) ? THEOREM_BG[meta.name] : PRIMARY[100];

  const fields = useMemo<readonly WitnessCardField[]>(() => {
    const base: WitnessCardField[] = [
      { label: "Code", value: meta.code, mono: true },
      { label: "Legacy name", value: meta.legacy, mono: true },
      { label: "Structure", value: meta.structure },
      { label: "Safety class", value: meta.safetyClass },
      { label: "Tier", value: `Tier ${String(meta.tier)}` },
      { label: "Generator", value: meta.generator },
      { label: "Seam type", value: meta.seamType },
      { label: "Agda source", value: meta.agdaSource, mono: true },
    ];
    if (capturedWitness !== undefined) {
      base.push({
        label: capturedWitness.label,
        value: `${capturedWitness.typeNotation} ⊨ ${capturedWitness.value}`,
        mono: true,
      });
    }
    if (stacking !== undefined) {
      base.push({
        label: `SDP ${stacking.sdp} — ${stacking.sdpName}`,
        value: `${stacking.dualityKind} · purity preserved`,
        mono: true,
      });
    }
    return base;
  }, [meta, capturedWitness, stacking]);

  const triggerKey = active
    ? `${meta.code}:${capturedWitness?.value ?? "idle"}:active`
    : `${meta.code}:idle`;

  return (
    <section
      aria-label={`Live ${meta.name} guarantee witness`}
      style={{
        marginTop: "1.5rem",
        display: "grid",
        gap: "0.75rem",
      }}
    >
      <header
        style={{
          display: "flex",
          alignItems: "center",
          gap: "0.625rem",
          padding: "0.625rem 0.875rem",
          borderRadius: "0.5rem",
          background: theoremBg,
          color: theoremColor,
          fontWeight: 600,
          fontSize: "0.875rem",
        }}
      >
        <ShieldCheck size={16} aria-hidden />
        <span>Live {meta.name} guarantee</span>
        <span
          style={{
            marginLeft: "auto",
            padding: "0.125rem 0.5rem",
            borderRadius: "9999px",
            background: modeLabel.bg,
            color: modeLabel.tone,
            fontSize: "0.6875rem",
            letterSpacing: "0.02em",
            textTransform: "uppercase",
          }}
        >
          {meta.mode}
        </span>
      </header>

      <WitnessEnter triggerKey={triggerKey}>
        <WitnessCard
          title={`${meta.name} (${meta.code}) — ${meta.structure}`}
          decisionLabel={modeLabel.copy}
          decisionTone={
            meta.mode === "immune" ? "success" : meta.mode === "contained" ? "warn" : "info"
          }
          fields={fields}
        />
      </WitnessEnter>

      {stacking !== undefined && (
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: "0.5rem",
            padding: "0.625rem 0.875rem",
            borderRadius: "0.5rem",
            border: `1px dashed ${GRAY[300]}`,
            background: GRAY[100],
            fontSize: "0.8125rem",
            color: GRAY[700],
            lineHeight: 1.5,
          }}
        >
          {stacking.level === 1 ? (
            <Layers size={14} aria-hidden style={{ marginTop: "0.125rem" }} />
          ) : (
            <Sigma size={14} aria-hidden style={{ marginTop: "0.125rem" }} />
          )}
          <span>
            <strong>
              SDP {stacking.sdp}/{stacking.sdpName}
            </strong>{" "}
            · level {stacking.level} · theorems {stacking.theorems.join(" ⊣ ")} ·{" "}
            {stacking.dualityKind}. Adding this guarantee to any composition preserves every
            previously inexpressible violation class (purity certificate emitted).
          </span>
        </div>
      )}
    </section>
  );
}

// ── Helpers for demo authors ───────────────────────────────────

/**
 * Quick-construct a captured-witness descriptor from a value and the
 * brand notation it carries. Demos can pass the result directly into
 * `<GuaranteeWitnessPanel capturedWitness={...} />`.
 */
export function describeWitness(
  label: string,
  typeNotation: string,
  value: unknown,
): { readonly label: string; readonly typeNotation: string; readonly value: string } {
  // `safeStringify` is the shared `@csk/demo-ui/utils` helper that
  // handles every awkward case (BigInt, symbols, circular refs, values
  // whose `JSON.stringify` collapses to `undefined`) so the
  // `WitnessCardField.value: string` invariant is preserved.
  return { label, typeNotation, value: safeStringify(value) };
}

/**
 * Convenience accessor: returns the `mode` field of a guarantee's meta.
 * Used by demos that want to switch styling on the neutralization mode
 * without re-deriving it from the meta themselves.
 */
export function guaranteeMode(meta: GuaranteeMeta): GuaranteeMeta["mode"] {
  return meta.mode;
}
